import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

export type ChatContextMode = 'related' | 'tangent' | 'general';

export type ChatRecipientKind = 'agent' | 'person';

export interface ChatRecipientSuggestion {
  id: string;
  kind: ChatRecipientKind;
  label: string;
  subtitle?: string;
}

export interface SurfaceChatContext {
  surfaceId: string;
  surfaceLabel: string;
  route: string;
  summary?: string;
  suggestedRecipients?: ChatRecipientSuggestion[];
  metadata?: Record<string, unknown>;
}

interface SurfaceContextValue {
  context: SurfaceChatContext | null;
  contextMode: ChatContextMode;
  setSurfaceContext: (context: SurfaceChatContext | null) => void;
  setContextMode: (mode: ChatContextMode) => void;
  effectiveContext: SurfaceChatContext;
}

const SurfaceContext = createContext<SurfaceContextValue | undefined>(undefined);

function routeFallbackLabel(pathname: string): string {
  if (pathname.includes('/control-bridge')) return 'Control Bridge';
  if (pathname.startsWith('/render/')) return 'Render surface';
  if (pathname.startsWith('/dashboard')) return 'Legacy dashboard';
  if (pathname.startsWith('/company-artifacts')) return 'Company artifacts';
  if (pathname.startsWith('/prime-artifacts')) return 'Prime artifacts';
  const segment = pathname.split('/').filter(Boolean)[0];
  return segment ? segment.replace(/-/g, ' ') : 'Application';
}

export const SurfaceContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pathname } = useLocation();
  const [context, setContextState] = useState<SurfaceChatContext | null>(null);
  const [contextMode, setContextMode] = useState<ChatContextMode>('related');

  const setSurfaceContext = useCallback((next: SurfaceChatContext | null) => {
    setContextState(next);
    if (next) {
      setContextMode('related');
    }
  }, []);

  const effectiveContext = useMemo(
    (): SurfaceChatContext =>
      context ?? {
        surfaceId: `route:${pathname}`,
        surfaceLabel: routeFallbackLabel(pathname),
        route: pathname,
      },
    [context, pathname]
  );

  const value = useMemo(
    () => ({
      context,
      contextMode,
      setSurfaceContext,
      setContextMode,
      effectiveContext,
    }),
    [context, contextMode, setSurfaceContext, effectiveContext]
  );

  return <SurfaceContext.Provider value={value}>{children}</SurfaceContext.Provider>;
};

export function useSurfaceContext(): SurfaceContextValue {
  const ctx = useContext(SurfaceContext);
  if (!ctx) {
    throw new Error('useSurfaceContext must be used within SurfaceContextProvider');
  }
  return ctx;
}
